import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy } from "lucide-react";
import api from "@/lib/api";
import { CoinIcon, RubyIcon } from "@/components/Icons";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

const PERIODS = ["daily", "weekly", "monthly"];
const PODIUM = [
  { ring: "#C0C0C8", crown: "/assets/crown-royal.png", badge: "linear-gradient(135deg,#D8D8E0,#8E8E99)" },
  { ring: "#E6B84C", crown: "/assets/crown-maharaja.png", badge: "linear-gradient(135deg,#FDE08B,#E6B84C)" },
  { ring: "#CD7F32", crown: "/assets/crown-noble.png", badge: "linear-gradient(135deg,#E0A060,#A05A2C)" },
];

function PodiumSpot({ row, rank, big, onClick }) {
  const p = PODIUM[rank === 1 ? 1 : rank === 2 ? 0 : 2];
  if (!row) return <div className="flex-1" />;
  return (
    <button onClick={onClick} data-testid={`podium-${rank}`} className="flex-1 flex flex-col items-center active:scale-95 transition-transform">
      <img src={p.crown} alt="" className={`object-contain ${big ? "w-12 h-12 -mb-1" : "w-9 h-9 -mb-0.5"} drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]`} />
      <div className="rounded-full p-[3px]" style={{ background: `linear-gradient(135deg, ${p.ring}, ${p.ring}88)`, boxShadow: `0 0 ${big ? 24 : 14}px ${p.ring}66` }}>
        <img src={row.user.avatar || "https://i.pravatar.cc/300?u=guest"} alt="" className={`rounded-full object-cover border-2 border-[#0B0F19] ${big ? "w-[84px] h-[84px]" : "w-16 h-16"}`} />
      </div>
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[#0B0F19] text-xs font-extrabold -mt-3 border-2 border-[#0B0F19]" style={{ background: p.badge }}>{rank}</span>
      <p className="text-white text-[11px] font-bold mt-1 truncate max-w-[100px]">{row.user.name}</p>
      <p className="text-[10px] font-bold flex items-center gap-1 text-[#E6B84C]"><CoinIcon size={11} /> {row.total.toLocaleString("en-IN")}</p>
    </button>
  );
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const [board, setBoard] = useState("senders");
  const [period, setPeriod] = useState("weekly");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(`/leaderboard?board=${board}&period=${period}`).then(({ data }) => setRows(data.leaderboard)).catch(() => setRows([]));
  }, [board, period]);

  const top3 = rows?.slice(0, 3) || [];
  const rest = rows?.slice(3) || [];

  return (
    <div className="min-h-screen max-w-md mx-auto pb-28 relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-52 bg-gradient-to-b from-[#2A1E4A]/60 to-transparent pointer-events-none" />

      <div className="relative flex items-center justify-between px-4 pt-6 pb-3">
        <button data-testid="leaderboard-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">LEADERBOARD</h1>
        <Trophy size={19} className="text-[#E6B84C]" />
      </div>

      <div className="relative px-4">
        <div className="flex glass rounded-full p-1 mb-3" data-testid="period-tabs">
          {PERIODS.map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
              className={`flex-1 py-2 rounded-full text-xs font-bold capitalize transition-colors ${period === p ? "bg-gradient-to-r from-[#8A2BE2] to-[#E6B84C] text-white shadow-[0_0_14px_rgba(230,184,76,0.4)]" : "text-zinc-500"}`}>
              {p}
            </button>
          ))}
        </div>

        <div className="flex mb-6 border-b border-white/10">
          {[["senders", "Top Gifters"], ["streamers", "Top Creators"]].map(([k, label]) => (
            <button key={k} data-testid={`board-${k}`} onClick={() => setBoard(k)}
              className={`flex-1 pb-2.5 text-sm font-bold transition-colors relative ${board === k ? "text-[#E6B84C]" : "text-zinc-500"}`}>
              {label}
              {board === k && <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-gradient-to-r from-[#8A2BE2] to-[#E6B84C]" />}
            </button>
          ))}
        </div>

        {rows === null && (
          <div className="space-y-2">
            <div className="flex justify-center gap-6 mb-6">
              {[0, 1, 2].map((i) => <div key={i} className={`skeleton rounded-full ${i === 1 ? "w-20 h-20" : "w-16 h-16 mt-4"}`} />)}
            </div>
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-2xl" />)}
          </div>
        )}
        {rows?.length === 0 && <p className="text-zinc-500 text-xs text-center py-12">No activity in this period yet</p>}

        {rows?.length > 0 && (
          <>
            <div className="flex items-end justify-center mb-2" data-testid="leaderboard-podium">
              <PodiumSpot row={top3[1]} rank={2} onClick={() => navigate(`/profile/${top3[1].user.id}`)} />
              <PodiumSpot row={top3[0]} rank={1} big onClick={() => navigate(`/profile/${top3[0].user.id}`)} />
              <PodiumSpot row={top3[2]} rank={3} onClick={() => navigate(`/profile/${top3[2]?.user.id}`)} />
            </div>
            <div className="h-3 mx-6 mb-5 rounded-t-2xl bg-gradient-to-r from-[#131A2A] via-[#1D2740] to-[#131A2A] border-t border-[#E6B84C]/30" />

            <div className="space-y-2" data-testid="leaderboard-list">
              {rest.map((r, i) => (
                <motion.div key={r.user.id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                  data-testid={`leaderboard-row-${r.user.id}`} onClick={() => navigate(`/profile/${r.user.id}`)}
                  className="flex items-center gap-3 rounded-2xl px-3.5 py-2.5 cursor-pointer active:scale-[0.99] transition-transform glass">
                  <span className="w-7 text-center font-extrabold text-base text-white">{i + 4}</span>
                  <Avatar src={r.user.avatar} size={38} level={r.user.level} tier={r.user.tier} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">{r.user.name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <span className="font-bold text-sm flex items-center gap-1 text-[#E6B84C]">
                      {board === "senders" ? <CoinIcon size={13} /> : <RubyIcon size={13} />} {r.total.toLocaleString("en-IN")}
                    </span>
                    <span className="text-[9px] font-bold text-zinc-400 flex items-center gap-1">👑 LV.{r.user.level}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
